import React from "react";
import { useTranslation } from "react-i18next";
import { HelpCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PaymentBrandLogo } from "@/features/settings/components/PaymentBrandLogo";
import {
  formatCardNumberWithCursor,
  formatExpirationDate,
} from "@/features/checkout/utils/validation";
import type { CardForm, CardFormField } from "../hooks/use-card-form";

/**
 * Kart formu — TEK kart formu.
 *
 * "Kayıtlı Kartlarım" ve checkout aynı bileşeni çizer; aralarındaki tek fark
 * `form.mode`'dur (bkz. `use-card-form.ts`). Etiketler, placeholder'lar,
 * doğrulama mesajları ve alan sırası bu dosyada bir kez yazılır.
 *
 * State bileşenin değil, çağıranın `useCardForm()` ile tuttuğu state'tir;
 * bileşen yalnızca çizer. İki ayrı ekranda iki ayrı örnek yaşadığı için
 * global store'a konmaz — ve zaten bir kart numarasının Zustand'a girmesi
 * istenmez.
 */

const INPUT_CLASS =
  "bg-white dark:bg-gray-800 border rounded-[10px] px-3 py-2 md:py-[10px] text-xs md:text-sm";
const LABEL_CLASS = "text-xs md:text-sm font-medium text-[#121926] dark:text-gray-100";

const inputBorder = (hasError: boolean) =>
  hasError ? "border-red-500" : "border-[#e3e8ef] dark:border-gray-700";

export const CardFields: React.FC<{ form: CardForm; idPrefix: string }> = ({ form, idPrefix }) => {
  const { t } = useTranslation();
  const { mode, values, errors, detectedBrand, set, touch } = form;
  const id = (name: string) => `${idPrefix}-${name}`;
  const cvcMax = detectedBrand === "amex" ? 4 : 3;

  const message = (field: CardFormField) =>
    errors[field] ? t(`checkout.validation.${field}.${errors[field]}`) : null;

  const expiryField = (
    <div className="flex-1 flex flex-col gap-1">
      <Label htmlFor={id("expiry")} className={LABEL_CLASS}>
        {t("checkout.expirationDate")}
      </Label>
      <Input
        id={id("expiry")}
        value={values.expirationDate}
        onChange={(e) => set({ expirationDate: formatExpirationDate(e.target.value) })}
        onBlur={() => touch("expirationDate")}
        placeholder="12/28"
        maxLength={5}
        inputMode="numeric"
        autoComplete="cc-exp"
        aria-invalid={!!errors.expirationDate}
        className={`${INPUT_CLASS} ${inputBorder(!!errors.expirationDate)}`}
      />
      <FieldError message={message("expirationDate")} />
    </div>
  );

  const nameField = (
    <div className="flex-1 flex flex-col gap-1">
      <Label htmlFor={id("name")} className={LABEL_CLASS}>
        {t("checkout.nameOnCard")}
      </Label>
      <Input
        id={id("name")}
        value={values.nameOnCard}
        onChange={(e) => set({ nameOnCard: e.target.value })}
        onBlur={() => touch("nameOnCard")}
        placeholder={t("checkout.nameOnCardPlaceholder")}
        autoComplete="cc-name"
        aria-invalid={!!errors.nameOnCard}
        className={`${INPUT_CLASS} ${inputBorder(!!errors.nameOnCard)}`}
      />
      <FieldError message={message("nameOnCard")} />
    </div>
  );

  const aliasField = (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id("alias")} className={LABEL_CLASS}>
        {t("checkout.cardAlias")} ({t("common.optional")})
      </Label>
      <Input
        id={id("alias")}
        value={values.cardAlias}
        onChange={(e) => set({ cardAlias: e.target.value })}
        placeholder={t("checkout.cardAliasPlaceholder")}
        maxLength={40}
        className={`${INPUT_CLASS} ${inputBorder(false)}`}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Kart numarası */}
      <div className="flex flex-col gap-1">
        <Label htmlFor={id("number")} className={LABEL_CLASS}>
          {t("checkout.cardNumber")}
        </Label>
        <div className="relative">
          <Input
            id={id("number")}
            value={values.cardNumber}
            onChange={(e) => {
              const input = e.target;
              const cursor = input.selectionStart || 0;
              const { formattedValue, newCursorPosition } = formatCardNumberWithCursor(
                e.target.value,
                cursor
              );
              set({ cardNumber: formattedValue });
              // React değeri yazdıktan sonra caret'i geri koy — yoksa ortaya
              // yazarken imleç sona fırlar.
              setTimeout(() => input.setSelectionRange(newCursorPosition, newCursorPosition), 0);
            }}
            onBlur={() => touch("cardNumber")}
            placeholder="4444 4444 4444 4444"
            maxLength={23}
            inputMode="numeric"
            autoComplete="cc-number"
            aria-invalid={!!errors.cardNumber}
            className={`pr-12 ${INPUT_CLASS} ${inputBorder(!!errors.cardNumber)}`}
          />
          {detectedBrand && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <PaymentBrandLogo brand={detectedBrand} className="w-8 h-6" />
            </div>
          )}
        </div>
        <FieldError message={message("cardNumber")} />
      </div>

      {mode === "pay" ? (
        <>
          {/* Son kullanma + CVV */}
          <div className="flex flex-col md:flex-row gap-4">
            {expiryField}
            <div className="flex-1 flex flex-col gap-1">
              <Label htmlFor={id("cvv")} className={LABEL_CLASS}>
                {t("checkout.cvv")}
              </Label>
              <div className="relative">
                <Input
                  id={id("cvv")}
                  type="password"
                  value={values.cvv}
                  onChange={(e) =>
                    set({ cvv: e.target.value.replace(/\D/g, "").slice(0, cvcMax) })
                  }
                  onBlur={() => touch("cvv")}
                  placeholder={cvcMax === 4 ? "1234" : "123"}
                  maxLength={cvcMax}
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  aria-invalid={!!errors.cvv}
                  className={`pr-8 ${INPUT_CLASS} ${inputBorder(!!errors.cvv)}`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              <FieldError message={message("cvv")} />
            </div>
          </div>

          {nameField}

          {/* Kartı kaydet → alias */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id={id("save")}
                checked={values.saveCard}
                onCheckedChange={(c) => set({ saveCard: c as boolean })}
                className="rounded-[4px] border-[#6172f3] data-[state=checked]:bg-[#6172f3] data-[state=checked]:border-[#6172f3]"
              />
              <Label htmlFor={id("save")} className={`${LABEL_CLASS} cursor-pointer`}>
                {t("checkout.saveCardDetails")}
              </Label>
            </div>
            {values.saveCard && <div className="pl-6">{aliasField}</div>}
          </div>
        </>
      ) : (
        <>
          {/* Son kullanma + isim */}
          <div className="flex flex-col md:flex-row gap-4">
            {expiryField}
            {nameField}
          </div>

          {aliasField}
        </>
      )}
    </div>
  );
};

const FieldError: React.FC<{ message: string | null }> = ({ message }) =>
  message ? (
    <p role="alert" className="text-xs text-red-500">
      {message}
    </p>
  ) : null;
